'use client';
import { splitNumberAndDialCode } from '@fshr/shared';
import { cn, getGeoIPCode } from '@fshr/shared-react';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import type { Country } from '@fshr/microservices/compiled/content.v1';
interface PhoneNumberProps {
	id?: string;
	name: string;
	label: string;
	required?: boolean;
	placeholder?: string;
	options?: any;
	value?: string;
	onChange?: (event: any) => void;
	onFocus?: (event: any) => void;
	onBlur?: (event: any) => void;
	validation?: boolean;
	errorMessage?: string;
	countries: Country[];
	disabled?: boolean;
	readonly?: boolean;
	wrapperClassName?: string;
	editableComponentWrapperClassName?: string;
	phoneClassName?: string;
	labelClassName?: string;
	borderClassName?: string;
	inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
	autoComplete?: React.InputHTMLAttributes<HTMLInputElement>['autoComplete'];
	autoCorrect?: React.InputHTMLAttributes<HTMLInputElement>['autoCorrect'];
}

const getInitialDialCode = (countries: Country[] = []) =>
	countries.find(country => country.code === getGeoIPCode())?.dialCode || countries[0]?.dialCode;

const PhoneNumber = forwardRef(
	(
		{
			id: initialId,
			name,
			label,
			required = false,
			placeholder,
			options: _options,
			value,
			onChange,
			onFocus,
			onBlur,
			errorMessage,
			countries = [],
			disabled,
			readonly = false,
			wrapperClassName,
			editableComponentWrapperClassName,
			phoneClassName,
			labelClassName,
			borderClassName,
			inputMode,
			autoComplete,
			autoCorrect,
		}: PhoneNumberProps,
		ref: React.Ref<HTMLInputElement | null>
	) => {
		const dialCodeSelectRef = useRef<HTMLSelectElement | null>(null);
		const phoneNumberInputRef = useRef<HTMLInputElement | null>(null);
		useImperativeHandle(ref, () => phoneNumberInputRef.current as HTMLInputElement, []);

		const { number: phoneNumberFromValue, dialCode: dialCodeFromValue } = splitNumberAndDialCode(
			value?.replace(/^\++/, ''),
			dialCodeSelectRef.current?.value ? [] : countries,
			dialCodeSelectRef.current?.value
		);

		const dialCodes = countries.map(({ dialCode, title }) => ({
			label: `${title} +${dialCode}`,
			value: dialCode || '',
		}));

		const handleDialCodeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
			event.preventDefault();
			onChange?.({
				currentTarget: {
					name,
					id,
					value: `${event.currentTarget.value}${phoneNumberInputRef.current?.value}`,
				},
				data: {
					dialCode: event.currentTarget.value,
					number: phoneNumberInputRef.current?.value,
				},
				forceValidation: phoneNumberInputRef.current?.value || errorMessage,
			});
		};

		const handleDialCodeBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
			event.preventDefault();
			if (phoneNumberInputRef.current !== event.relatedTarget) {
				onBlur?.({
					currentTarget: {
						name,
						id,
						value: `${event.currentTarget.value}${phoneNumberInputRef.current?.value}`,
					},
					data: {
						dialCode: event.currentTarget.value,
						number: phoneNumberInputRef.current?.value,
					},
					forceValidation: true,
				});
			}
		};

		const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			event.preventDefault();
			const newPhoneValue = event.currentTarget?.value?.replace(/\D+/, '');

			const combinedValue = `${dialCodeSelectRef.current?.value}${newPhoneValue}`;

			if (value !== combinedValue) {
				onChange?.({
					currentTarget: {
						name,
						id,
						value: combinedValue,
					},
					data: {
						dialCode: dialCodeSelectRef.current?.value,
						number: newPhoneValue,
					},
				});
			}
		};

		const handlePhoneNumberBlur = (event: React.FocusEvent<HTMLInputElement>) => {
			event.preventDefault();
			if (dialCodeSelectRef.current !== event.relatedTarget) {
				onBlur?.({
					currentTarget: {
						name,
						id,
						value: `${dialCodeSelectRef.current?.value}${event.currentTarget.value}`,
					},
					data: {
						dialCode: dialCodeSelectRef.current?.value,
						number: event.currentTarget.value,
					},
				});
			}
		};

		const placeholderToDisplay = required ? (placeholder || label) + '*' : placeholder || label;

		const id = initialId || `field--${name}`;

		return (
			<div
				className={cn(
					'relative w-full pb-stack-l',
					{
						'pt-200': label,
						'text-red-700': errorMessage,
					},
					wrapperClassName
				)}
			>
				{readonly ? (
					<div
						className={cn('relative', {
							'pt-200': label,
							'pb-200': readonly,
						})}
					>
						<span className="text-body2 text-text-primary">
							+{dialCodeFromValue} {phoneNumberFromValue}
						</span>
						{label && (
							<label
								className="text-label uppercase absolute duration-300 opacity-100 left-0 top-0"
								htmlFor={id}
							>
								{`${label}${required ? '*' : ''}`}
							</label>
						)}
					</div>
				) : (
					<>
						{' '}
						<div className={cn('relative', editableComponentWrapperClassName)}>
							<div
								className={cn(
									[
										'h-[47px]',
										'w-full',
										'px-100',
										'lg:px-150',
										'pt-060',
										'bg-surface-form',
										'border-b',
										'flex-col',
										'justify-start',
										'items-start',
										'gap-100',
										'inline-flex',
										'border-border-default',
										'mt-020',
									],
									{
										'border-red-700': errorMessage,
									},
									phoneClassName
								)}
							>
								<div className="self-stretch justify-start gap-x-inline-m inline-flex">
									<div
										className={cn(
											'justify-start items-center gap-x-inline-s flex border-r pr-150 border-border-default',
											{
												'border-red-700': errorMessage,
												hidden: disabled,
											},
											borderClassName
										)}
									>
										<div
											className={cn(
												[
													'bg-transparent absolute cursor-pointer text-body2 text-ellipsis w-[90px] whitespace-nowrap pt-[5px] border-b-0 h-auto flex items-center',

													// arrow
													'after:pointer-events-none',
													"after:content-['']",
													'after:border-[6px]',
													'after:border-l-transparent',
													'after:border-r-transparent',
													'after:border-b-0',
													'after:border-t-inherit',
													'after:absolute',
													'after:top-1/2',
													'after:-translate-y-1/2',
													'ltr:after:right-0',
													'rtl:after:left-0',
												],
												{
													'border-red-700 after:text-red-700': errorMessage,
												}
											)}
											onClick={() => dialCodeSelectRef.current?.focus()}
										>
											{`+${dialCodeFromValue || getInitialDialCode(countries)}`}
										</div>
										<select
											className="bg-transparent cursor-pointer text-body2 text-ellipsis w-[90px] whitespace-nowrap pt-[5px] border-b-0 h-auto text-transparent z-10"
											name={[name, 'areaCode'].filter(Boolean).join('__')}
											id={[id, 'areaCode'].filter(Boolean).join('__')}
											value={dialCodeFromValue || getInitialDialCode(countries)}
											onChange={handleDialCodeChange}
											onBlur={handleDialCodeBlur}
											required={required}
											disabled={disabled}
											ref={el => {
												dialCodeSelectRef.current = el;
											}}
											aria-label={label && `${label} country code`}
										>
											{dialCodes.map(({ label, value }) => (
												<option
													className="text-text-heading"
													key={`${value}-${label}-${Math.random()}`}
													value={value}
												>
													{/* eslint-disable-next-line no-restricted-syntax -- dial-code label, not CMS copy; <option> content is text-only and can't hold a <span> */}
													{label}
												</option>
											))}
										</select>
									</div>
									<div className="justify-start items-start w-full flex pt-040">
										<input
											className={cn(
												[
													'peer',
													'text-body2',
													'bg-transparent',
													'w-full',
													'placeholder:transition-opacity',
													'placeholder:opacity-100!',
													'placeholder:duration-300!',
													'placeholder:text-black!',
													'focus:placeholder:opacity-0!',
												],
												{
													'placeholder:text-red-700!': errorMessage,
													'text-red-700': errorMessage,
												}
											)}
											type="tel"
											name={name}
											id={id}
											value={disabled ? value : phoneNumberFromValue}
											placeholder={placeholderToDisplay}
											onChange={handlePhoneNumberChange}
											onFocus={onFocus}
											onBlur={handlePhoneNumberBlur}
											required={required}
											disabled={disabled}
											ref={phoneNumberInputRef}
											aria-labelledby={label && `${id}-label`}
											inputMode={inputMode}
											autoComplete={autoComplete}
											autoCorrect={autoCorrect}
										/>
										{label && (
											<label
												className={
													'text-label uppercase absolute duration-300 opacity-100 left-0 top-0 -translate-y-180 peer-placeholder-shown:opacity-0 peer-placeholder-shown:translate-y-0 peer-focus:opacity-100 peer-focus:-translate-y-180'
												}
												htmlFor={id}
												id={id && `${id}-label`}
											>
												<span
													className={cn(labelClassName, {
														'text-red-700': errorMessage,
													})}
												>{`${label}${required ? '*' : ''}`}</span>
											</label>
										)}
									</div>
								</div>
							</div>
						</div>
						{errorMessage && (
							<span className="absolute lg:pt-020 text-caption text-red-700" role="alert">
								{errorMessage}
							</span>
						)}
					</>
				)}
			</div>
		);
	}
);

export default PhoneNumber;
